// mds-dojo — dojo/store/Observable. Wraps a store so query() results carry an
// observe(listener, includeUpdates) method; put/add/remove notify observers with
// (object, removedFrom, insertedInto) so live views (StoreListLite) re-render.

interface Observer {
  query: any;
  options: any;
  results: any[];
  listener: (object: any, removedFrom: number, insertedInto: number) => void;
  includeUpdates: boolean;
}

// dojo/store/Observable is invoked both as `Observable(store)` and
// `new Observable(store)` (both return the wrapped store); expose a type with
// call AND construct signatures so either form type-checks.
interface ObservableFn {
  (store: any): any;
  new (store: any): any;
}

const Observable = function Observable(store: any): any {
  const observers: Observer[] = [];
  const originalQuery = store.query;
  const originalPut = store.put;
  const originalAdd = store.add;
  const originalRemove = store.remove;

  function queryEngineMatches(query: any, obj: any): boolean {
    if (typeof query === 'function') { return !!query([obj]).length; }
    if (!query || Object.keys(query).length === 0) { return true; }
    for (const k in query) { if (obj[k] !== query[k]) { return false; } }
    return true;
  }

  store.query = function (query: any, options: any) {
    const results: any = originalQuery.call(store, query, options);
    results.observe = function (listener: any, includeUpdates: boolean) {
      const observer: Observer = { query, options, results, listener, includeUpdates: !!includeUpdates };
      observers.push(observer);
      return {
        remove() {
          const i = observers.indexOf(observer);
          if (i !== -1) { observers.splice(i, 1); }
        },
      };
    };
    return results;
  };

  function notify(object: any, existingId: any, isRemove: boolean) {
    for (const observer of observers.slice()) {
      const matches = !isRemove && queryEngineMatches(observer.query, object);
      // Recompute this observer's result set from the current store contents.
      const fresh = originalQuery.call(store, observer.query, observer.options);
      let insertedInto = -1;
      for (let i = 0; i < fresh.length; i++) {
        if (store.getIdentity(fresh[i]) === (isRemove ? existingId : store.getIdentity(object))) { insertedInto = i; break; }
      }
      const removedFrom = isRemove ? 0 : (matches ? -1 : -1);
      observer.results = fresh;
      observer.listener(object, isRemove ? 0 : removedFrom, isRemove ? -1 : insertedInto);
    }
  }

  store.put = function (object: any, options: any) {
    const id = originalPut.call(store, object, options);
    notify(object, id, false);
    return id;
  };

  store.add = function (object: any, options: any) {
    const id = (originalAdd || originalPut).call(store, object, options);
    notify(object, id, false);
    return id;
  };

  store.remove = function (id: any) {
    const removed = store.get(id);
    const result = originalRemove.call(store, id);
    notify(removed, id, true);
    return result;
  };

  return store;
} as unknown as ObservableFn;

export default Observable;
